import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface HouseholdBudgetFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
}

// Income categories
const INCOME_CATEGORIES = [
  { id: "nettoeinkommen", label: "Nettoeinkommen" },
  { id: "mieteinnahmen", label: "Mieteinnahmen" },
  { id: "kinderbeihilfe", label: "Kinderbeihilfe" },
  { id: "sonstige_einnahmen", label: "Sonstiges:" },
];

// Expense categories
const EXPENSE_CATEGORIES = [
  { id: "wohnen", label: "Wohnen (Miete, Betriebskosten, Energie, etc.)" },
  { id: "mobilitaet", label: "Mobilität (KFZ, Tank, Reparatur, Öffis, etc.)" },
  { id: "versicherungen", label: "Versicherungen (UV, KV, KFZ, HHV, RS, BU, etc.)" },
  { id: "haushalt", label: "Haushalt (Lebensmittel, Körperpflege, etc.)" },
  { id: "lifestyle", label: "Lifestyle (Hobbies, Ausflüge, Sport, Restaurants, etc.)" },
  { id: "kinder", label: "Kinder (Kinderbetreuung, Schule, Freizeit, etc.)" },
  { id: "medien", label: "Medien (Telefon, Internet, PC, Software, etc.)" },
  { id: "kredite", label: "Kredite, Leasing & andere Verbindlichkeiten" },
  { id: "sonstige_ausgaben", label: "Sonstiges:" },
];

export function HouseholdBudgetForm({
  formValues,
  onFieldChange,
  className,
}: HouseholdBudgetFormProps) {
  const income: Record<string, string> = formValues.budget_income || {};
  const expenses: Record<string, string> = formValues.budget_expenses || {};

  const updateIncome = (id: string, value: string) => {
    const updated = { ...income, [id]: value };
    onFieldChange("budget_income", updated);
  };

  const updateExpense = (id: string, value: string) => {
    const updated = { ...expenses, [id]: value };
    onFieldChange("budget_expenses", updated);
  };

  // Calculate totals
  const totalIncome = useMemo(() => {
    return Object.values(income).reduce((sum, val) => {
      const num = parseFloat(val) || 0;
      return sum + num;
    }, 0);
  }, [income]);

  const totalExpenses = useMemo(() => {
    return Object.values(expenses).reduce((sum, val) => {
      const num = parseFloat(val) || 0;
      return sum + num;
    }, 0);
  }, [expenses]);

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Ihre </span>
          <span className="text-primary">Haushaltsrechnung</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          So behalten Sie Ihre Finanzen im Griff.
        </p>
      </div>

      {/* Income Table */}
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left p-3 font-semibold">Einnahmen</th>
                <th className="text-right p-3 font-semibold w-32">€ / Monat</th>
              </tr>
            </thead>
            <tbody>
              {INCOME_CATEGORIES.map((category) => (
                <tr key={category.id} className="border-t">
                  <td className="p-3 font-medium text-sm">{category.label}</td>
                  <td className="p-2">
                    <Input
                      type="number"
                      value={income[category.id] || ""}
                      onChange={(e) => updateIncome(category.id, e.target.value)}
                      className="h-8 text-sm text-right"
                      placeholder=""
                    />
                  </td>
                </tr>
              ))}
              <tr className="border-t bg-muted/30">
                <td className="p-3 font-semibold text-sm">Summe Einnahmen</td>
                <td className="p-3 font-semibold text-right">
                  € {totalIncome.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left p-3 font-semibold">Ausgaben</th>
                <th className="text-right p-3 font-semibold w-32">€ / Monat</th>
              </tr>
            </thead>
            <tbody>
              {EXPENSE_CATEGORIES.map((category) => (
                <tr key={category.id} className="border-t">
                  <td className="p-3 font-medium text-sm">{category.label}</td>
                  <td className="p-2">
                    <Input
                      type="number"
                      value={expenses[category.id] || ""}
                      onChange={(e) => updateExpense(category.id, e.target.value)}
                      className="h-8 text-sm text-right"
                      placeholder=""
                    />
                  </td>
                </tr>
              ))}
              <tr className="border-t bg-muted/30">
                <td className="p-3 font-semibold text-sm">Summe Ausgaben</td>
                <td className="p-3 font-semibold text-right">
                  € {totalExpenses.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Net Result */}
      <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
        <div className="flex justify-between items-center">
          <Label className="font-semibold text-lg">Freies Kapital (Einnahmen - Ausgaben)</Label>
          <span className={cn(
            "font-bold text-lg",
            totalIncome - totalExpenses >= 0 ? "text-green-600" : "text-red-600"
          )}>
            € {(totalIncome - totalExpenses).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  );
}
